// File: client_serena/src/pages/UserDashboard.js
// To use the Serena logo as a loading spinner:
// import Logo from '../logo.svg';
// <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="teal.500" size="xl">
//   <img src={Logo} alt="Serena" style={{ height: 32, width: 32 }} />
// </Spinner>
import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut, onIdTokenChanged } from "firebase/auth";
import {
Box,
Button,
Text,
VStack,
HStack,
Spinner,
Heading,
Stack,
Tabs,
TabList,
Tab,
TabPanels,
TabPanel,
useToast,
} from "@chakra-ui/react";

import { fetchUserData } from "../api/fetchUserData";
import { fetchMealPlan } from "../api/fetchMealPlan";
import EditProfileForm from "../components/EditProfileForm";
import UserMealPlan from "../components/UserMealPlan";
import MacroBanner from "../components/MacroBanner";
import HealthMetricsSection from "../components/HealthMetricsSection";
import UserRejectedMeals from "../components/UserRejectedMeals";
import BiomarkerGenomeUploadSection from "../components/BiomarkerGenomeUploadSection";
import SelenseHealthMemory from "../components/SelenseHealthMemory";

function UserDashboard() {
const navigate = useNavigate();
const toast = useToast();
const toastShownRef = useRef(false);

const [user, setUser] = useState(null);
const [mongoUser, setMongoUser] = useState(null);
const [mealPlan, setMealPlan] = useState({});
const [loading, setLoading] = useState(true);
const [healthMetrics, setHealthMetrics] = useState(null);

useEffect(() => {
const unsubscribe = onIdTokenChanged(getAuth(), async (firebaseUser) => {
if (!firebaseUser) return navigate("/login");
setUser(firebaseUser);
});
return () => unsubscribe();
}, [navigate]);

useEffect(() => {
  const loadData = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const mongoData = await fetchUserData(token);
      setMongoUser(mongoData);
      setHealthMetrics(mongoData.healthMetrics || {});

      const plan = await fetchMealPlan();
      setMealPlan(plan);

      const fixedPlan = Array.isArray(plan) && plan.length === 1 ? plan[0] : plan;
      setMealPlan(fixedPlan);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      if (!toastShownRef.current) {
        toast({
          title: "Error loading dashboard",
          description: "Please check your connection.",
          status: "error",
          isClosable: true,
        });
        toastShownRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  };
  loadData();
}, [user, toast]);

const handleLogout = async () => {
try {
await signOut(getAuth());
navigate("/login");
} catch {
toast({ title: "Logout failed", status: "error", isClosable: true });
}
};

const refetchMealPlan = async () => {
const updated = await fetchMealPlan();
setMealPlan(updated);
};

if (loading) return <Spinner size="xl" mt="10" color="teal.500" />;

return (
  <Box minH="100vh" bgGradient="linear(to-br, green.50, white 80%)" py={10} px={2}>
    <Box maxW="1000px" mx="auto" bg="white" borderRadius="2xl" boxShadow="2xl" p={{ base: 4, md: 8 }}>
      <HStack justify="space-between" mb={6}>
        <Heading size="xl" color="green.700" fontWeight="extrabold">
          Welcome, {user?.displayName || mongoUser?.name || "User"}!
        </Heading>
        <Button colorScheme="red" onClick={handleLogout} borderRadius="xl" fontWeight="bold" boxShadow="md">
          Logout
        </Button>
      </HStack>
      <Box mb={6}>
        <SelenseHealthMemory />
      </Box>

      <Tabs variant="enclosed" colorScheme="green" isFitted>
        <TabList mb={4}>
          <Tab fontWeight="bold">Meal Plan</Tab>
          <Tab fontWeight="bold">Genome & Biomarkers</Tab>
          <Tab fontWeight="bold">Profile</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Stack spacing={8}>
              <Box bgGradient="linear(to-br, white, green.50 90%)" p={6} boxShadow="lg" borderRadius="xl" border="1px solid" borderColor="gray.200">
                <Heading size="lg" mb={2} color="green.700" fontWeight="bold">
                  🍲 Your Hyper-Personalized Meal Plan — Straight From the Kitchen, Made Just for You
                </Heading>
                <Text fontSize="md" color="gray.600" mb={4}>
                  Handpicked from 150+ delicious Nigerian meals — made with love, matched to your body, goals and taste. No wahala, just better food for your health.
                </Text>
                <MacroBanner
                  healthMetrics={mongoUser?.healthMetrics}
                  userGoals={mongoUser?.preferences?.goals || []}
                />
                <UserMealPlan
                  mealPlan={mealPlan}
                  setMealPlan={setMealPlan}
                  purchasedMeals={mongoUser?.purchasedMeals || []}
                  favoriteMeals={mongoUser?.favoriteMeals || []}
                  setPurchasedMeals={(list) =>
                    setMongoUser((prev) => ({ ...prev, purchasedMeals: list }))
                  }
                />
                <UserRejectedMeals rejected={mongoUser?.rejectedMeals || []} />
              </Box>
            </Stack>
          </TabPanel>
          <TabPanel>
            <Box bgGradient="linear(to-br, white, green.50 90%)" p={6} borderRadius="xl" boxShadow="md" border="1px solid #e2e8f0">
              <BiomarkerGenomeUploadSection
                mongoUser={mongoUser}
                user={user}
                setMongoUser={setMongoUser}
              />
            </Box>
          </TabPanel>
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Box bg="gray.50" borderRadius="xl" boxShadow="sm" p={5}>
                <Heading size="md" mb={2} color="green.700">Health Check Panel</Heading>
                <HealthMetricsSection
                  existingMetrics={healthMetrics}
                  onUpdated={(updated) => {
                    setHealthMetrics(updated);
                    refetchMealPlan();
                    toast({ title: "Health metrics updated", status: "success", isClosable: true });
                  }}
                />
              </Box>
              <Box bg="gray.50" borderRadius="xl" boxShadow="sm" p={5}>
                <Heading size="md" mb={2} color="green.700">Preferences</Heading>
                {mongoUser?.preferences && (
                  <EditProfileForm
                    preferences={mongoUser.preferences}
                    setMongoUser={setMongoUser}
                    mongoUser={mongoUser}
                    setMealPlan={setMealPlan}
                    refetchMealPlan={refetchMealPlan}
                  />
                )}
              </Box>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  </Box>
);
}

export default UserDashboard;



